// Central routing helpers. The app uses react-router (BrowserRouter) as the
// single source of truth for navigation. These helpers map the legacy screen
// names to URL paths and provide a navigator that works outside React
// components (e.g. the axios wrapper's 401 handler in Apicall.js).

// Screen name -> URL path (single source of truth for routes)
export const SCREEN_PATHS = {
  Login: "/login",
  Dashboard: "/dashboard",
  SearchedTicket: "/tickets/client",
  // Same screen/component as SearchedTicket, reached from the BMR menu
  // instead of the Client menu — see the SoftwareTickets/BMRSoftwareTickets
  // pair below for why this is a distinct route rather than a query param.
  BMRSearchedTicket: "/bmr/tickets/client",
  SoftwareTickets: "/tickets/software",
  // Same screen/component as SoftwareTickets, reached from the BMR menu
  // instead of the Software menu — a distinct route (rather than a query
  // param) so the two entry points get independent, refresh-safe active-nav
  // highlighting and the component can tell which context it's in (see the
  // `viewContext` prop passed to it in App.js).
  BMRSoftwareTickets: "/bmr/tickets/software",
  SupportTickets: "/tickets/support",
  SoftwareAnalytics: "/analytics/software",
  SupportAnalytics: "/analytics/support",
  ChecklistAnalytics: "/analytics/checklist",
  Attendance: "/attendance",
  Checklist: "/checklist",
  ActivePractices: "/practices",
  ProjectStatus: "/project-status",
  ProSoftwareTeam: "/prosoftware-team",
  ChangeRequestForm: "/change-request",
  Main: "/settings",
  // BMR-only, all-practices rollup dashboard (Charges vs Payment, Rejections,
  // Denials, AR Aging) — see the "Managment Board" entry in Header/Index.js.
  ManagementBoard: "/bmr/management-board",
  // Service ID: #3867 — Shared route for both biller report views.
  BillerSummary: "/bmr/biller-summary",
};

const PATH_TO_SCREEN = Object.fromEntries(
  Object.entries(SCREEN_PATHS).map(([screen, path]) => [path, screen])
);

export const DEFAULT_PATH = SCREEN_PATHS.Dashboard;

// Shared Software pages keep BMR navigation context in the URL so refresh,
// bookmarks, and browser back/forward preserve the originating view.
export const isBmrSharedView = ({ pathname, search }) =>
  [SCREEN_PATHS.ProjectStatus, SCREEN_PATHS.ProSoftwareTeam].includes(pathname) &&
  new URLSearchParams(search).get("view") === "bmr";

export const pathForScreen = (screen) => SCREEN_PATHS[screen] || DEFAULT_PATH;

export const screenForPath = (pathname) =>
  PATH_TO_SCREEN[pathname] || PATH_TO_SCREEN[DEFAULT_PATH];

// Registered react-router navigate function. Registered once by App (which is
// rendered inside <BrowserRouter>). Lets plain modules (Apicall.js) navigate.
let navigateFn = null;
export const registerNavigate = (fn) => {
  navigateFn = fn;
};

export const navigateTo = (to, options) => {
  if (navigateFn) navigateFn(to, options);
};

// Build a URL like "/tickets/software?taskId=123&status=Done".
// Falsy/null params are omitted.
export const buildUrl = (screen, params = {}) => {
  const path = pathForScreen(screen);
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      search.set(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

// Navigate to a screen (with optional query params) via the registered navigator.
export const goToScreen = (screen, params) => {
  navigateTo(buildUrl(screen, params));
};
